/** @file Socket-bone-space mount of a mounted attachment (§5.9) through core's itemMountTransform. */
import { handleToLocal } from '@pose2d/core/load/handleToLocal';
import { meshPixelToLocal } from '@pose2d/core/load/meshPixelToLocal';
import { regionPixelToLocal } from '@pose2d/core/load/regionPixelToLocal';
import { itemMountTransform } from '@pose2d/core/mount/itemMountTransform';
import { MOUNT_REASONS } from '@pose2d/core/mount/mountReasons.data';
import { throwError } from '@pose2d/format/throwError';
import type { AttachmentDoc, Doc, ImageDoc } from '@pose2d/format/types';
import { trig } from '@pose2d/math/trig';
import type { Affine } from '@pose2d/math/types';
import { handleArray } from './handleArray';

/** The item's authored pixel -> slot-bone frame, exactly as core's loader rebuilds it from the rig record. */
function authoredFrame(att: AttachmentDoc, image: ImageDoc): Affine {
  if (att.type === 'region') {
    const { x, y, rotation, scaleX, scaleY, width: w, height: h } = att;
    return regionPixelToLocal({ x, y, rotation, scaleX, scaleY, w, h, origW: image.w, origH: image.h });
  }
  return meshPixelToLocal(att.uvs, image.w, image.h, att.geometry.kind === 'rigid' ? att.geometry.vertices : null);
}

/**
 * null when `att` has no mount. Otherwise M = itemMountTransform(handle in the item's authored frame, |b − a| of the
 * socket, fitLength): the similarity carrying the item's own local frame (region transform with width/height and
 * scale, or rigid-mesh fit) onto the socket bone, handle base → origin, tip → +X — the same mount runtime equip
 * computes (final review C2/C7, ruling b: the authored size wins).
 */
export function mountMatrix(doc: Doc, id: string, att: AttachmentDoc, image: ImageDoc): Affine | null {
  const mount = att.mount;
  if (mount === undefined) return null;
  const path = `attachments.${id}.mount`;
  const socket = doc.sockets[mount.socket];
  if (socket === undefined) throwError('NOT_FOUND', { kind: 'socket', ref: mount.socket }, { path: `${path}.socket` });
  if (att.handle === undefined) throwError('MOUNT_INVALID', { attachment: att.name, reason: MOUNT_REASONS.noHandle }, { path });
  if (att.type === 'mesh' && att.geometry.kind === 'skinned') throwError('MOUNT_INVALID', { attachment: att.name, reason: MOUNT_REASONS.skinned }, { path });
  const length = trig.hypot(socket.b[0] - socket.a[0], socket.b[1] - socket.a[1]);
  const handle = handleToLocal(handleArray(att.handle), authoredFrame(att, image));
  return itemMountTransform(new Float64Array(6), handle, length, mount.fitLength);
}
